import { BodyComponent, HeaderComponent } from '@comps/components';
import {
  ApartmentOutlined,
  BankOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  RightOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';
import { Avatar, Button, Card, Empty, Segmented, Skeleton, Spin, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { makeRequest, makeRequestStateless, notify, NOTIFICATION_TYPE } from '@utils/helpers';
import ViewProfile from '../../employeeManagement/companyEmployees/components/ViewProfile';
import {
  getEmployeeHierarchyChildrenReq,
  getEmployeeHierarchyReq,
  getHrmsHierarchyNodeReq,
  getHrmsHierarchyReq,
  getOrganogramExportReq
} from './requests';
import { downloadOrganogramPdf } from './organogramPdf';
import './chart.css';

const { Paragraph, Text, Title } = Typography;

const pageConfig = {
  headers: {
    title: 'Organization Hierarchy',
    breadcrumb: []
  }
};

const hierarchySteps = [
  { key: 'company', label: 'Company', icon: <ApartmentOutlined /> },
  { key: 'location', label: 'Locations', icon: <EnvironmentOutlined /> },
  { key: 'branch', label: 'Branches', icon: <BankOutlined /> },
  { key: 'department', label: 'Departments', icon: <SolutionOutlined /> },
  { key: 'hod', label: 'HOD', icon: <UserSwitchOutlined /> },
  { key: 'manager', label: 'Line Managers', icon: <TeamOutlined /> },
  { key: 'employee', label: 'Employees', icon: <UserOutlined /> }
];

const metricConfig = [
  { key: 'location_count', label: 'Locations', icon: <EnvironmentOutlined /> },
  { key: 'branch_count', label: 'Branches', icon: <BankOutlined /> },
  { key: 'department_count', label: 'Departments', icon: <SolutionOutlined /> },
  { key: 'employee_count', label: 'Employees', icon: <TeamOutlined /> }
];

const hierarchyViewOptions = [
  { label: 'Organization Level', value: 'department' },
  { label: 'Employee Level', value: 'employee' }
];

const typeIconMap = {
  company: <ApartmentOutlined />,
  location: <EnvironmentOutlined />,
  branch: <BankOutlined />,
  department: <SolutionOutlined />,
  hod: <UserSwitchOutlined />,
  manager: <TeamOutlined />,
  employee: <UserOutlined />
};

const getPersonSubtitle = (person) => {
  if (!person) {
    return 'Not assigned';
  }

  return person.designation || person.job_title || person.department_name || person.code || 'Employee';
};

const getPersonDisplayName = (person) => {
  if (!person) {
    return 'Not assigned';
  }

  if (person.name && person.code) {
    return `${person.name} (${person.code})`;
  }

  return person.name || person.code || 'Not assigned';
};

const PersonCard = ({ person, type = 'employee', extra, onClick }) => {
  const icon = typeIconMap[type] || typeIconMap.employee;
  const subtitle = getPersonSubtitle(person);

  return (
    <div className={ `hrms-person-card hrms-person-card--${type} ${onClick ? 'hrms-person-card--clickable' : ''}` } onClick={ onClick }>
      <div className="hrms-person-card__header">
        <Avatar
          size={ 44 }
          src={ person?.profile_pic_url || null }
          icon={ !person?.profile_pic_url ? icon : null }
          className="hrms-person-card__avatar"
        />
        <div>
          <div className="hrms-person-card__role">{ type === 'hod' ? 'Department Head' : type === 'manager' ? 'Line Manager' : 'Employee' }</div>
          <div className="hrms-person-card__name">{ getPersonDisplayName(person) }</div>
          <div className="hrms-person-card__subtitle">{ subtitle }</div>
        </div>
      </div>
      { extra }
    </div>
  );
};

const DiagramNode = ({ icon, title, subtitle, type, meta, onClick, expanded, loading, actionLabel }) => (
  <div className={ `hrms-diagram-node hrms-diagram-node--${type}` }>
    <div className="hrms-diagram-node__icon">{ icon }</div>
    <div className="hrms-diagram-node__content">
      <div className="hrms-diagram-node__title">{ title }</div>
      { subtitle && <div className="hrms-diagram-node__subtitle">{ subtitle }</div> }
      { meta && <div className="hrms-diagram-node__meta">{ meta }</div> }
    </div>
    { onClick && (
      <button type="button" className="hrms-diagram-node__action" onClick={ onClick }>
        <span>{ loading ? 'Loading...' : actionLabel }</span>
        { loading ? <LoadingOutlined /> : expanded ? <DownOutlined /> : <RightOutlined /> }
      </button>
    ) }
  </div>
);

const DiagramEmployeeNode = ({ person, label, meta, onClick }) => (
  <button type="button" className="hrms-diagram-employee" onClick={ onClick }>
    <Avatar size={ 28 } src={ person?.profile_pic_url || null } icon={ !person?.profile_pic_url ? <UserOutlined /> : null } />
    <div>
      <div className="hrms-diagram-employee__name">{ getPersonDisplayName(person) }</div>
      <div className="hrms-diagram-employee__role">{ label }</div>
      { meta && <div className="hrms-diagram-employee__meta">{ meta }</div> }
    </div>
  </button>
);

const EmployeeListCard = ({ title, employees, onOpenProfile }) => {
  if (!employees?.length) {
    return null;
  }

  return (
    <div className="hrms-employee-list-card">
      <div className="hrms-employee-list-card__title">{ title }</div>
      <ul className="hrms-employee-list">
        { employees.map((employee) => (
          <li key={ employee.id }>
            <button type="button" className="hrms-employee-list__button" onClick={ () => onOpenProfile(employee) }>
              { getPersonDisplayName(employee) }
            </button>
            <Text type="secondary">{ employee.designation || employee.job_title || employee.department_name || employee.code || '' }</Text>
          </li>
        )) }
      </ul>
    </div>
  );
};

const DiagramEmployeeGroup = ({ title, employees, limit = 4, onOpenProfile }) => {
  const [expanded, setExpanded] = useState(false);

  if (!employees?.length) {
    return null;
  }

  const visibleEmployees = expanded ? employees : employees.slice(0, limit);
  const remaining = employees.length - visibleEmployees.length;

  return (
    <div className="hrms-diagram__people-col">
      <div className="hrms-diagram__label">{ title }</div>
      <div className="hrms-diagram__team">
        { visibleEmployees.map((employee) => (
          <DiagramEmployeeNode
            key={ employee.id }
            person={ employee }
            label={ getPersonSubtitle(employee) }
            onClick={ () => onOpenProfile(employee) }
          />
        )) }
        { remaining > 0 && (
          <button type="button" className="hrms-diagram__more" onClick={ () => setExpanded(true) }>
            +{ remaining } more
          </button>
        ) }
        { expanded && employees.length > limit && (
          <button type="button" className="hrms-diagram__less" onClick={ () => setExpanded(false) }>
            Show less
          </button>
        ) }
      </div>
    </div>
  );
};

const updateLocationNodes = (locations, locationId, updater) =>
  locations.map((location) => (location.id !== locationId ? location : updater(location)));

const updateBranchNodes = (locations, locationId, branchId, updater) =>
  locations.map((location) => {
    if (location.id !== locationId) {
      return location;
    }

    return {
      ...location,
      branches: (location.branches || []).map((branch) => (branch.id !== branchId ? branch : updater(branch))),
    };
  });

const updateDepartmentNodes = (locations, locationId, branchId, departmentId, updater) =>
  locations.map((location) => {
    if (location.id !== locationId) {
      return location;
    }

    return {
      ...location,
      branches: (location.branches || []).map((branch) => {
        if (branch.id !== branchId) {
          return branch;
        }

        return {
          ...branch,
          departments: (branch.departments || []).map((department) => (department.id !== departmentId ? department : updater(department))),
        };
      }),
    };
  });

const updateEmployeeTreeById = (node, idToFind, updater) => {
  if (!node) {
    return node;
  }

  if (node.id === idToFind) {
    return updater(node);
  }

  if (!node.children?.length) {
    return node;
  }

  return {
    ...node,
    children: node.children.map((child) => updateEmployeeTreeById(child, idToFind, updater)),
  };
};

const ExpandButton = ({ expanded, loading, onClick, label }) => (
  <button type="button" className="hrms-expand-button" onClick={ onClick }>
    <span>{ loading ? 'Loading...' : label }</span>
    { loading ? <LoadingOutlined /> : expanded ? <DownOutlined /> : <RightOutlined /> }
  </button>
);

const DraggableBoard = ({ children }) => {
  const boardRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const [zoom, setZoom] = useState(1);

  const updateZoom = (delta) => {
    setZoom((current) => {
      const next = Number((current + delta).toFixed(2));
      return Math.min(1.6, Math.max(0.6, next));
    });
  };

  const onMouseDown = (event) => {
    const board = boardRef.current;
    if (!board) {
      return;
    }

    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: board.scrollLeft,
      scrollTop: board.scrollTop,
    };

    board.classList.add('hrms-board--dragging');
  };

  const onMouseMove = (event) => {
    const board = boardRef.current;
    const drag = dragRef.current;
    if (!board || !drag.active) {
      return;
    }

    board.scrollLeft = drag.scrollLeft - (event.clientX - drag.startX);
    board.scrollTop = drag.scrollTop - (event.clientY - drag.startY);
  };

  const endDrag = () => {
    const board = boardRef.current;
    dragRef.current.active = false;
    if (board) {
      board.classList.remove('hrms-board--dragging');
    }
  };

  return (
    <div className="hrms-board-shell">
      <div className="hrms-board-toolbar">
        <button type="button" className="hrms-board-toolbar__btn" onClick={ () => updateZoom(-0.1) }>
          -
        </button>
        <div className="hrms-board-toolbar__value">{ Math.round(zoom * 100) }%</div>
        <button type="button" className="hrms-board-toolbar__btn" onClick={ () => updateZoom(0.1) }>
          +
        </button>
        <button type="button" className="hrms-board-toolbar__reset" onClick={ () => setZoom(1) }>
          Reset
        </button>
      </div>
      <div ref={ boardRef } className="hrms-board" onMouseDown={ onMouseDown } onMouseMove={ onMouseMove } onMouseUp={ endDrag } onMouseLeave={ endDrag }>
        <div className="hrms-board-zoom" style={ { transform: `scale(${zoom})` } }>
          { children }
        </div>
      </div>
    </div>
  );
};

const EmployeeHierarchyNode = ({ node, onToggle, onOpenProfile }) => {
  const isCompany = !!node.is_company;
  const hasChildren = !node.is_leaf;
  const teamCount = Number(node.team_count || 0);

  return (
    <div className="hrms-employee-tree__group">
      <div className={ `hrms-employee-tree__node ${isCompany ? 'hrms-employee-tree__node--company' : ''}` }>
        <div className="hrms-employee-tree__node-head">
          <Avatar
            size={ 40 }
            src={ !isCompany ? node.profile_pic_url || null : null }
            icon={ isCompany ? <ApartmentOutlined /> : !node.profile_pic_url ? <UserOutlined /> : null }
          />
          <div className="hrms-employee-tree__content">
            <div className="hrms-employee-tree__name">{ isCompany ? node.name : `${node.name || ''}${node.title ? ` (${node.title})` : ''}` }</div>
            <div className="hrms-employee-tree__meta">
              { isCompany ? 'Reporting root' : node.extras?.map((item) => `${item.key}: ${item.value}`).join(' | ') || 'Employee' }
            </div>
            { !isCompany && teamCount > 0 && (
              <div className="hrms-employee-tree__badges">
                <Tag className="hrms-employee-tree__badge" color="blue">
                  { `${teamCount} ${teamCount === 1 ? 'direct subordinate' : 'direct subordinates'}` }
                </Tag>
              </div>
            ) }
          </div>
        </div>
        <div className="hrms-employee-tree__actions">
          { node.employment_id && node.employee_id && (
            <Button size="small" onClick={ () => onOpenProfile({ id: node.employment_id, employee_id: node.employee_id }) }>
              View Profile
            </Button>
          ) }
          { hasChildren && (
            <Button size="small" type="primary" ghost onClick={ () => onToggle(node) }>
              { node.loading_children ? 'Loading...' : node.children_loaded ? (node.expanded ? 'Hide Team' : 'Show Team') : 'Load Team' }
            </Button>
          ) }
        </div>
      </div>

      { node.expanded && node.children?.length > 0 && (
        <div className="hrms-employee-tree__children">
          <div className="hrms-employee-tree__children-line" />
          <div className="hrms-employee-tree__children-list">
            { node.children.map((child) => (
              <EmployeeHierarchyNode key={ child.id } node={ child } onToggle={ onToggle } onOpenProfile={ onOpenProfile } />
            )) }
          </div>
        </div>
      ) }
    </div>
  );
};

const DepartmentHierarchyDiagram = ({
  company,
  locations,
  loadLocationBranches,
  loadBranchDepartments,
  loadDepartmentDetails,
  openProfile,
  registerDiagramNode,
}) => (
  <Card className="hrms-visual-card" bordered={ false }>
    <div className="hrms-flow-card__title">Interactive Department Hierarchy</div>
    <Paragraph className="hrms-visual-card__hint">
      Drag anywhere on the canvas to pan. Click nodes to load the next level without forcing the whole page to overflow.
    </Paragraph>
    <DraggableBoard>
      <div className="hrms-diagram">
        <div className="hrms-diagram__root">
          <DiagramNode
            icon={ <ApartmentOutlined /> }
            title={ company.name || 'Organization' }
            subtitle="Company"
            meta={ company.short_name || 'Single company structure' }
            type="company"
          />
        </div>
        <div className="hrms-diagram__root-link" />

        <div className="hrms-diagram__locations">
          { locations.map((location) => (
            <div key={ location.id } className="hrms-diagram__location-group" ref={ registerDiagramNode(`location-${location.id}`) }>
              <div className="hrms-diagram__connector hrms-diagram__connector--vertical hrms-diagram__connector--top" />
              <DiagramNode
                icon={ <EnvironmentOutlined /> }
                title={ location.name }
                subtitle="Location"
                meta={ `${location.branch_count || 0} branches` }
                type="location"
                onClick={ () => loadLocationBranches(location.id) }
                expanded={ !!location.expanded }
                loading={ !!location.loading_children }
                actionLabel="Open"
              />

              { location.expanded && (
                <div className="hrms-diagram__branches">
                  <div className="hrms-diagram__sibling-line" />
                  { location.branches?.map((branch) => (
                    <div key={ branch.id } className="hrms-diagram__branch-group" ref={ registerDiagramNode(`branch-${branch.id}`) }>
                      <div className="hrms-diagram__connector hrms-diagram__connector--vertical hrms-diagram__connector--top" />
                      <DiagramNode
                        icon={ <BankOutlined /> }
                        title={ branch.name }
                        subtitle="Branch"
                        meta={ `${branch.department_count || 0} departments` }
                        type="branch"
                        onClick={ () => loadBranchDepartments(location.id, branch.id) }
                        expanded={ !!branch.expanded }
                        loading={ !!branch.loading_children }
                        actionLabel="Open"
                      />

                      { branch.expanded && (
                        <div className="hrms-diagram__departments">
                          <div className="hrms-diagram__sibling-line" />
                          { branch.departments?.map((department) => (
                            <div key={ department.id } className="hrms-diagram__department-group" ref={ registerDiagramNode(`department-${department.id}`) }>
                              <div className="hrms-diagram__connector hrms-diagram__connector--vertical hrms-diagram__connector--top" />
                              <DiagramNode
                                icon={ <SolutionOutlined /> }
                                title={ department.name }
                                subtitle="Department"
                                meta={ `${department.employee_count || 0} staff` }
                                type="department"
                                onClick={ () => loadDepartmentDetails(location.id, branch.id, department.id) }
                                expanded={ !!department.expanded }
                                loading={ !!department.loading_children }
                                actionLabel="Open"
                              />

                              { department.expanded && (
                                <div className="hrms-diagram__reporting">
                                  <div className="hrms-diagram__connector hrms-diagram__connector--vertical hrms-diagram__connector--top" />
                                  <div className="hrms-diagram__people-row">
                                    <div className="hrms-diagram__people-col">
                                      <div className="hrms-diagram__label">HOD</div>
                                      <DiagramEmployeeNode person={ department.hod } label={ getPersonSubtitle(department.hod) } onClick={ () => openProfile(department.hod) } />
                                    </div>

                                    { !!department.line_managers?.length && (
                                      <div className="hrms-diagram__people-col">
                                        <div className="hrms-diagram__label">Line Managers</div>
                                        <div className="hrms-diagram__pillars">
                                          { department.line_managers.map((item) => (
                                            <div key={ item.manager.id } className="hrms-diagram__pillar">
                                              <DiagramEmployeeNode
                                                person={ item.manager }
                                                label={ getPersonSubtitle(item.manager) }
                                                meta={ `${item.employee_count || 0} team members` }
                                                onClick={ () => openProfile(item.manager) }
                                              />
                                              <DiagramEmployeeGroup title="Team" employees={ item.employees } onOpenProfile={ openProfile } />
                                            </div>
                                          )) }
                                        </div>
                                      </div>
                                    ) }

                                    { !!department.cross_department_managers?.length && (
                                      <div className="hrms-diagram__people-col">
                                        <div className="hrms-diagram__label">Cross-Department Managers</div>
                                        <div className="hrms-diagram__pillars">
                                          { department.cross_department_managers.map((item) => (
                                            <div key={ item.manager.id } className="hrms-diagram__pillar">
                                              <DiagramEmployeeNode
                                                person={ item.manager }
                                                label={ getPersonSubtitle(item.manager) }
                                                meta={ item.manager?.department_name ? `${item.manager.department_name} department` : 'Other department' }
                                                onClick={ () => openProfile(item.manager) }
                                              />
                                              <DiagramEmployeeGroup title="Team" employees={ item.employees } onOpenProfile={ openProfile } />
                                            </div>
                                          )) }
                                        </div>
                                      </div>
                                    ) }

                                    { !!department.direct_employees?.length && (
                                      <DiagramEmployeeGroup title="Direct Employees" employees={ department.direct_employees } limit={ 5 } onOpenProfile={ openProfile } />
                                    ) }
                                  </div>
                                </div>
                              ) }
                            </div>
                          )) }
                        </div>
                      ) }
                    </div>
                  )) }
                </div>
              ) }
            </div>
          )) }
        </div>
      </div>
    </DraggableBoard>
  </Card>
);

const IndexOrgChart = (props) => {
  const [loader, setLoader] = useState(false);
  const [hierarchy, setHierarchy] = useState(null);
  const [employeeHierarchy, setEmployeeHierarchy] = useState(null);
  const [employeeHierarchyLoading, setEmployeeHierarchyLoading] = useState(false);
  const [fullModeLoading, setFullModeLoading] = useState(false);
  const [isFullMode, setIsFullMode] = useState(false);
  const [profileRecord, setProfileRecord] = useState(null);
  const [activeHierarchyView, setActiveHierarchyView] = useState('department');
  const [pdfExporting, setPdfExporting] = useState(false);
  const diagramNodeRefs = useRef({});

  const openProfile = (person) => {
    const record = person?.employment_id && person?.employee_id ? { id: person.employment_id, employee_id: person.employee_id } : person?.id && person?.employee_id ? { id: person.id, employee_id: person.employee_id } : null;
    if (!record) {
      return;
    }

    setProfileRecord(record);
  };

  const registerDiagramNode = (key) => (element) => {
    if (element) {
      diagramNodeRefs.current[key] = element;
      return;
    }

    delete diagramNodeRefs.current[key];
  };

  const focusDiagramNode = (key) => {
    window.requestAnimationFrame(() => {
      const element = diagramNodeRefs.current[key];
      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    });
  };

  useEffect(() => {
    makeRequest(setLoader, getHrmsHierarchyReq, null, (res) => setHierarchy(res), () => setHierarchy(null));
  }, []);

  useEffect(() => {
    if (activeHierarchyView === 'employee' && !employeeHierarchy) {
      makeRequest(
        setEmployeeHierarchyLoading,
        getEmployeeHierarchyReq,
        null,
        (res) => setEmployeeHierarchy(res || null),
        () => setEmployeeHierarchy(null)
      );
    }
  }, [activeHierarchyView, employeeHierarchy]);

  const fetchNode = async (type, id) => {
    let payload = null;
    await makeRequestStateless(
      getHrmsHierarchyNodeReq,
      { type, id },
      (res) => {
        payload = res;
      },
      () => {
        payload = null;
      }
    );
    return payload;
  };

  const loadLocationBranches = (locationId) => {
    focusDiagramNode(`location-${locationId}`);

    setHierarchy((current) => ({
      ...current,
      locations: updateLocationNodes(current?.locations || [], locationId, (location) => {
        if (location.children_loaded || location.loading_children) {
          return { ...location, expanded: !location.expanded };
        }

        return { ...location, expanded: true, loading_children: true };
      }),
    }));

    const target = hierarchy?.locations?.find((location) => location.id === locationId);
    if (target?.children_loaded || target?.loading_children) {
      return;
    }

    makeRequest(
      () => { },
      getHrmsHierarchyNodeReq,
      { type: 'location', id: locationId },
      (res) => {
        setHierarchy((current) => ({
          ...current,
          locations: updateLocationNodes(current?.locations || [], locationId, (location) => ({
            ...location,
            branches: res.children || [],
            children_loaded: true,
            expanded: true,
            loading_children: false,
          })),
        }));
        focusDiagramNode(`location-${locationId}`);
      },
      () => {
        setHierarchy((current) => ({
          ...current,
          locations: updateLocationNodes(current?.locations || [], locationId, (location) => ({ ...location, loading_children: false })),
        }));
      }
    );
  };

  const loadBranchDepartments = (locationId, branchId) => {
    focusDiagramNode(`branch-${branchId}`);

    setHierarchy((current) => ({
      ...current,
      locations: updateBranchNodes(current?.locations || [], locationId, branchId, (branch) => {
        if (branch.children_loaded || branch.loading_children) {
          return { ...branch, expanded: !branch.expanded };
        }

        return { ...branch, expanded: true, loading_children: true };
      }),
    }));

    const location = hierarchy?.locations?.find((item) => item.id === locationId);
    const target = location?.branches?.find((branch) => branch.id === branchId);
    if (target?.children_loaded || target?.loading_children) {
      return;
    }

    makeRequest(
      () => { },
      getHrmsHierarchyNodeReq,
      { type: 'branch', id: branchId },
      (res) => {
        setHierarchy((current) => ({
          ...current,
          locations: updateBranchNodes(current?.locations || [], locationId, branchId, (branch) => ({
            ...branch,
            departments: res.children || [],
            children_loaded: true,
            expanded: true,
            loading_children: false,
          })),
        }));
        focusDiagramNode(`branch-${branchId}`);
      },
      () => {
        setHierarchy((current) => ({
          ...current,
          locations: updateBranchNodes(current?.locations || [], locationId, branchId, (branch) => ({ ...branch, loading_children: false })),
        }));
      }
    );
  };

  const loadDepartmentDetails = (locationId, branchId, departmentId) => {
    focusDiagramNode(`department-${departmentId}`);

    setHierarchy((current) => ({
      ...current,
      locations: updateDepartmentNodes(current?.locations || [], locationId, branchId, departmentId, (department) => {
        if (department.children_loaded || department.loading_children) {
          return { ...department, expanded: !department.expanded };
        }

        return { ...department, expanded: true, loading_children: true };
      }),
    }));

    const location = hierarchy?.locations?.find((item) => item.id === locationId);
    const branch = location?.branches?.find((item) => item.id === branchId);
    const target = branch?.departments?.find((department) => department.id === departmentId);
    if (target?.children_loaded || target?.loading_children) {
      return;
    }

    makeRequest(
      () => { },
      getHrmsHierarchyNodeReq,
      { type: 'department', id: departmentId },
      (res) => {
        setHierarchy((current) => ({
          ...current,
          locations: updateDepartmentNodes(current?.locations || [], locationId, branchId, departmentId, (department) => ({
            ...department,
            ...res.details,
            children_loaded: true,
            expanded: true,
            loading_children: false,
          })),
        }));
        focusDiagramNode(`department-${departmentId}`);
      },
      () => {
        setHierarchy((current) => ({
          ...current,
          locations: updateDepartmentNodes(current?.locations || [], locationId, branchId, departmentId, (department) => ({ ...department, loading_children: false })),
        }));
      }
    );
  };

  const loadFullHierarchy = async () => {
    if (!hierarchy?.locations?.length || fullModeLoading) {
      return;
    }

    setFullModeLoading(true);
    setIsFullMode(true);

    try {
      const locations = [];

      for (const location of hierarchy.locations) {
        const locationRes = await fetchNode('location', location.id);
        const branches = locationRes?.children || [];
        const enrichedBranches = [];

        for (const branch of branches) {
          const branchRes = await fetchNode('branch', branch.id);
          const departments = branchRes?.children || [];
          const enrichedDepartments = [];

          for (const department of departments) {
            const departmentRes = await fetchNode('department', department.id);
            enrichedDepartments.push({
              ...department,
              ...(departmentRes?.details || {}),
              children_loaded: true,
              expanded: true,
              loading_children: false,
            });
          }

          enrichedBranches.push({
            ...branch,
            departments: enrichedDepartments,
            children_loaded: true,
            expanded: true,
            loading_children: false,
          });
        }

        locations.push({
          ...location,
          branches: enrichedBranches,
          children_loaded: true,
          expanded: true,
          loading_children: false,
        });
      }

      setHierarchy((current) => ({ ...current, locations }));
    } finally {
      setFullModeLoading(false);
    }
  };

  const collapseFullHierarchy = () => {
    setIsFullMode(false);
    setHierarchy((current) => ({
      ...current,
      locations: (current?.locations || []).map((location) => ({
        ...location,
        expanded: false,
        branches: (location.branches || []).map((branch) => ({
          ...branch,
          expanded: false,
          departments: (branch.departments || []).map((department) => ({ ...department, expanded: false })),
        })),
      })),
    }));
  };

  const toggleEmployeeNode = (node) => {
    if (!node) {
      return;
    }

    setEmployeeHierarchy((current) => updateEmployeeTreeById(current, node.id, (currentNode) => {
      if (currentNode.children_loaded || currentNode.loading_children) {
        return { ...currentNode, expanded: !currentNode.expanded };
      }

      return { ...currentNode, expanded: true, loading_children: true };
    }));

    if (node.children_loaded || node.loading_children) {
      return;
    }

    makeRequest(
      () => { },
      getEmployeeHierarchyChildrenReq,
      { id: node.id },
      (res) => {
        setEmployeeHierarchy((current) => updateEmployeeTreeById(current, node.id, (currentNode) => ({
          ...currentNode,
          children: res.children || [],
          children_loaded: true,
          expanded: true,
          loading_children: false,
        })));
      },
      () => {
        setEmployeeHierarchy((current) => updateEmployeeTreeById(current, node.id, (currentNode) => ({ ...currentNode, loading_children: false })));
      }
    );
  };

  // Fetches its own fully expanded tree, so the on-screen expand/collapse state is untouched.
  const exportOrganogramPdf = async () => {
    setPdfExporting(true);
    try {
      const res = await getOrganogramExportReq();
      if (!(res?.code === 200 || res?.success === true) || !res?.data?.root) {
        throw new Error(res?.message);
      }

      // Let the button's loading state paint before the synchronous PDF build.
      await new Promise((resolve) => setTimeout(resolve, 0));
      downloadOrganogramPdf({ companyName: res.data.company_name, root: res.data.root });
    } catch (e) {
      notify('Export failed', 'The organogram PDF could not be generated. Please try again, and contact support if the problem continues.', NOTIFICATION_TYPE.ERROR);
    } finally {
      setPdfExporting(false);
    }
  };

  const summary = hierarchy?.summary || {};
  const company = hierarchy?.company || {};
  const locations = hierarchy?.locations || [];

  return (
    <>
      <HeaderComponent headers={ pageConfig.headers } modkey={ props.modkey } />
      { profileRecord && <ViewProfile record={ profileRecord } onCompleted={ () => setProfileRecord(null) } /> }
      <BodyComponent>
        <div className="hrms-org-hero">
          <div>
            <div className="hrms-org-hero__eyebrow">HRMS</div>
            <Title level={ 1 }>Organization Hierarchy</Title>
            <Paragraph>
              { activeHierarchyView === 'department'
                ? 'Complete company structure with departments, HODs, line managers, cross-department reporting, and employee groups.'
                : 'Employee-focused reporting hierarchy to inspect manager chains, subordinates, and team structures from the people perspective.' }
            </Paragraph>
          </div>
          {/* { activeHierarchyView === 'department' && (
            <div className="hrms-org-hero__actions">
              <Button type="primary" onClick={ loadFullHierarchy } loading={ fullModeLoading }>
                Show Full Hierarchy
              </Button>
              <Button onClick={ collapseFullHierarchy } disabled={ !isFullMode && !fullModeLoading }>
                Collapse View
              </Button>
            </div>
          ) } */}
          <div className="hrms-org-metrics">
            { metricConfig.map((metric) => (
              <div key={ metric.key } className="hrms-org-metric">
                <div className="hrms-org-metric__icon">{ metric.icon }</div>
                <div className="hrms-org-metric__value">{ summary[metric.key] || 0 }</div>
                <div className="hrms-org-metric__label">{ metric.label }</div>
              </div>
            )) }
          </div>
        </div>

        <div className="hrms-org-page">
          <Card className="hrms-level-switcher" bordered={ false }>
            <div className="hrms-level-switcher__row">
              <div>
                <div className="hrms-org-hero__eyebrow">Hierarchy View</div>
                <Title level={ 4 } style={ { marginBottom: 4 } }>Choose Perspective</Title>
                <Paragraph style={ { marginBottom: 0 } }>
                  `Organization Level` shows structure. `Employee Level` shows reporting lines, subordinates, and who is under whom.
                </Paragraph>
              </div>
              <div className="hrms-level-switcher__controls">
                <Segmented options={ hierarchyViewOptions } value={ activeHierarchyView } onChange={ setActiveHierarchyView } />
                <Button type="primary" icon={ <FilePdfOutlined /> } loading={ pdfExporting } onClick={ exportOrganogramPdf }>
                  { pdfExporting ? 'Generating PDF...' : 'Export PDF' }
                </Button>
                <Tag color={ activeHierarchyView === 'department' ? 'blue' : 'green' }>{ activeHierarchyView === 'department' ? 'Organization Level' : 'Employee Level' }</Tag>
              </div>
            </div>
          </Card>

          { activeHierarchyView === 'department' && !!locations.length && (
            <DepartmentHierarchyDiagram
              company={ company }
              locations={ locations }
              loadLocationBranches={ loadLocationBranches }
              loadBranchDepartments={ loadBranchDepartments }
              loadDepartmentDetails={ loadDepartmentDetails }
              openProfile={ openProfile }
              registerDiagramNode={ registerDiagramNode }
            />
          ) }

          { activeHierarchyView === 'employee' && (
            <Card className="hrms-visual-card" bordered={ false }>
              <div className="hrms-flow-card__title">Interactive Employee Reporting Hierarchy</div>
              <Paragraph className="hrms-visual-card__hint">
                This separate feature focuses on employee reporting only: who reports to whom, subordinate chains, and manager teams across the company.
              </Paragraph>
              <Skeleton loading={ employeeHierarchyLoading } active paragraph={ { rows: 6 } }>
                { !employeeHierarchy && <Empty description="No employee hierarchy available." /> }
                { employeeHierarchy && (
                  <DraggableBoard>
                    <div className="hrms-employee-tree">
                      <EmployeeHierarchyNode node={ employeeHierarchy } onToggle={ toggleEmployeeNode } onOpenProfile={ openProfile } />
                    </div>
                  </DraggableBoard>
                ) }
              </Skeleton>
            </Card>
          ) }


          <Card className="hrms-flow-card" bordered={ false }>
            <div className="hrms-flow-card__title">{ activeHierarchyView === 'department' ? 'Hierarchy Flow' : 'Employee Reporting Flow' }</div>
            <div className="hrms-flow-steps">
              { hierarchySteps.map((step) => (
                <div key={ step.key } className="hrms-flow-step">
                  <div className="hrms-flow-step__icon">{ step.icon }</div>
                  <div className="hrms-flow-step__label">{ step.label }</div>
                </div>
              )) }
            </div>
          </Card>

          { activeHierarchyView === 'department' && (
            <Skeleton loading={ loader } active paragraph={ { rows: 8 } }>
              { !locations.length && <Empty description="No organization hierarchy data available." /> }
              <div className="hrms-location-stack">
                { locations.map((location) => (
                  <section key={ location.id } className="hrms-location-section">
                    <Card className="hrms-location-card" bordered={ false }>
                      <div className="hrms-section-heading">
                        <div>
                          <div className="hrms-section-heading__eyebrow">Location</div>
                          <Title level={ 3 }>{ location.name }</Title>
                          <Paragraph>{ location.branch_count || 0 } branches and { location.employee_count || 0 } employees</Paragraph>
                        </div>
                        <div className="hrms-section-heading__actions">
                          <Tag color="blue">{ location.code || 'Location' }</Tag>
                          <ExpandButton expanded={ !!location.expanded } loading={ !!location.loading_children } onClick={ () => loadLocationBranches(location.id) } label="Load branches" />
                        </div>
                      </div>

                      { location.expanded && (
                        <Spin spinning={ !!location.loading_children }>
                          <div className="hrms-branch-grid">
                            { location.children_loaded && !location.branches?.length && <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No branches mapped in this location." /> }

                            { location.branches?.map((branch) => (
                              <Card key={ branch.id } className="hrms-branch-card" bordered={ false }>
                                <div className="hrms-branch-card__header">
                                  <div>
                                    <div className="hrms-branch-card__eyebrow">Branch</div>
                                    <Title level={ 4 }>{ branch.name }</Title>
                                    <div className="hrms-branch-card__meta">
                                      <Tag>{ branch.code || 'Branch' }</Tag>
                                      <Tag color="green">{ branch.employee_count || 0 } employees</Tag>
                                      <Tag color="gold">{ branch.department_count || 0 } departments</Tag>
                                    </div>
                                  </div>
                                  <ExpandButton expanded={ !!branch.expanded } loading={ !!branch.loading_children } onClick={ () => loadBranchDepartments(location.id, branch.id) } label="Load departments" />
                                </div>

                                { branch.manager && (
                                  <div className="hrms-branch-manager">
                                    <Text strong>Branch Manager</Text>
                                    <PersonCard person={ branch.manager } type="manager" onClick={ () => openProfile(branch.manager) } />
                                  </div>
                                ) }

                                { branch.expanded && (
                                  <Spin spinning={ !!branch.loading_children }>
                                    <div className="hrms-department-grid">
                                      { branch.children_loaded && !branch.departments?.length && <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No departments mapped in this branch." /> }

                                      { branch.departments?.map((department) => (
                                        <div key={ department.id } className="hrms-department-card">
                                          <div className="hrms-department-card__top">
                                            <div>
                                              <div className="hrms-department-card__eyebrow">Department</div>
                                              <div className="hrms-department-card__title">{ department.name }</div>
                                            </div>
                                            <ExpandButton expanded={ !!department.expanded } loading={ !!department.loading_children } onClick={ () => loadDepartmentDetails(location.id, branch.id, department.id) } label="Load reporting" />
                                          </div>

                                          <div className="hrms-department-card__meta">
                                            <Tag>{ department.code || 'Department' }</Tag>
                                            <Tag color="orange">{ department.employee_count || 0 } staff</Tag>
                                            <Tag color="magenta">{ department.manager_count || 0 } managers</Tag>
                                          </div>

                                          { department.hod && (
                                            <div className="hrms-department-card__preview">
                                              <div className="hrms-node-title">HOD</div>
                                              <PersonCard person={ department.hod } type="hod" onClick={ () => openProfile(department.hod) } />
                                            </div>
                                          ) }

                                          { department.expanded && (
                                            <Spin spinning={ !!department.loading_children }>
                                              <div className="hrms-department-card__section">
                                                <div className="hrms-node-title">HOD</div>
                                                <PersonCard person={ department.hod } type="hod" onClick={ () => openProfile(department.hod) } />
                                              </div>

                                              { !!department.direct_employees?.length && (
                                                <div className="hrms-department-card__section">
                                                  <div className="hrms-node-title">Direct Reports to HOD</div>
                                                  <EmployeeListCard title="Employees reporting directly to HOD" employees={ department.direct_employees } onOpenProfile={ openProfile } />
                                                </div>
                                              ) }

                                              <div className="hrms-department-card__section">
                                                <div className="hrms-node-title">Line Managers</div>
                                                <div className="hrms-line-manager-grid">
                                                  { department.line_managers?.length ? department.line_managers.map((item) => (
                                                    <div key={ item.manager.id } className="hrms-line-manager-card">
                                                      <PersonCard person={ item.manager } type="manager" onClick={ () => openProfile(item.manager) } extra={ <Tag color="blue">{ item.employee_count || 0 } team members</Tag> } />
                                                      <EmployeeListCard title="Team" employees={ item.employees } onOpenProfile={ openProfile } />
                                                    </div>
                                                  )) : <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No line managers mapped." /> }
                                                </div>
                                              </div>

                                              { !!department.cross_department_managers?.length && (
                                                <div className="hrms-department-card__section">
                                                  <div className="hrms-node-title">Cross-Department Reporting</div>
                                                  <div className="hrms-line-manager-grid">
                                                    { department.cross_department_managers.map((item) => (
                                                      <div key={ item.manager.id } className="hrms-line-manager-card">
                                                        <PersonCard person={ item.manager } type="manager" onClick={ () => openProfile(item.manager) } extra={ <Tag color="purple">{ item.manager?.department_name || 'Other department' }</Tag> } />
                                                        <EmployeeListCard title="Employees reporting to this manager" employees={ item.employees } onOpenProfile={ openProfile } />
                                                      </div>
                                                    )) }
                                                  </div>
                                                </div>
                                              ) }
                                            </Spin>
                                          ) }
                                        </div>
                                      )) }
                                    </div>
                                  </Spin>
                                ) }
                              </Card>
                            )) }
                          </div>
                        </Spin>
                      ) }
                    </Card>
                  </section>
                )) }
              </div>
            </Skeleton>
          ) }
        </div>
      </BodyComponent>
    </>
  );
};

export default IndexOrgChart;
